import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { CourseFormat, TransferDto } from '../../api/quotas';
import { COURSE_FORMATS, useTransferCreate, useTransfers } from '../../api/quotas';
import { usePartnerPoolPatch } from '../../api/partners';
import { patchVenue } from '../../api/venues';
import { VENUE_PLANS_ROOT, useVenuePlans, type VenuePlanDto, type VenuePlanListEnvelope } from '../../api/venue-plans';
import { useAuth } from '../../auth/AuthContext';
import { hasAtLeast } from '../../auth/roles';
import { Combobox } from '../../components/Combobox';
import { ExportButton } from '../../components/ExportButton';
import { Badge, Button, EmptyState, ErrorState, Field, Input, Modal, Select, Textarea } from '../../components/ui';
import { TableWrap } from '../../components/TableWrap';
import {
  DEPARTMENT_BUCKET_LABEL,
  departmentFilterOptions,
  parseDepartmentBucket,
  rowsInDepartmentBucket,
  type DepartmentBucket,
} from '../../departments';
import { useDepartmentJoin } from '../../departments-query';
import { formatCount, formatDateTime } from '../dashboard/format';
import { useQueryParams, toInt, type ParamValue } from '../../lib/query-params';

import './quotas.css';
import { useVenueDirectory } from './dictionary';
import {
  PARAM,
  PLAN_COLUMNS,
  PLAN_SORT_FIELDS,
  firstClickDirection,
  parsePlanInput,
  partnerPoolCaption,
  partnerPoolsOfDirectory,
  partnerVenueIdsOf,
  planChanged,
  planTotals,
  planVenueOptions,
  rowsWithoutPartnerVenues,
  sortPlanRows,
  transferDirection,
  transferDirectionLabel,
  transferRoute,
  transferScopeLabel,
  venueCardHref,
  venueComboboxOptions,
  venueLevelHint,
  venueNameOf,
  validateTransferDraft,
  emptyTransferDraft,
  type IdNameOption,
  type PartnerPoolEntry,
  type PlanSortField,
  type QuotaGridRow,
  type TransferDraft,
  type TransferField,
} from './quotas-model';
import { parsePoolInput, poolChanged, poolLowWarning } from '../settings/partner-pool-model';

/**
 * Quotas & transfers (И6, task T.C4b) — REWORKED for owner wave 7 (T.V1).
 *
 * The plan of a place is TWO numbers — offline and online, set per place, with no
 * programme attached (the storage moved to `venue_plans`, migration `0010_venue_plans`) —
 * so the grid is `Площадка | План офлайн | План онлайн | Итого`, and that is all of it:
 * no programme columns, no programme filter. Both plan columns are editable in place for
 * an ADMIN, through PATCH #8 (`planOffline`/`planOnline`, the same write the venue card
 * uses), and the venue name opens the place's own page.
 *
 * The read is `GET /api/venue-plans` (12.5) — one row per venue, the whole roster, so the
 * filters are client-side and the «Площадка» combobox filters without a second request.
 * #12 stays on the wire as the deprecated legacy surface; this page stopped reading it.
 *
 * ADDITIVE (T.Z56) — the «Отдел» filter is client-side for the same reason: the read
 * answers the whole venue-joined roster in one shot, the api takes no query, and
 * `planTotals` already recomputes from the surviving rows. A venue's department is its
 * managers' departments (`src/departments.ts`, the same fold the ЦА grid applies);
 * the paged surfaces (#3/#7.5/#11) narrow these buckets server-side instead.
 *
 * ADDITIVE (T.Z149, owner 20.09) — the partner filials are NOT rows of this grid any
 * more: a filial holds no quota of its own, `partners.plan_online` is the ONE shared
 * number, so a per-filial row misrepresents the group. Their rows leave (the fold is
 * the #6 directory's `kind`), and the shared pool arrives as the banner block above
 * the grid — captioned «Академия Топ: общая квота 2 600», editable in place by an
 * ADMIN through #29, the same audited write the settings tab makes.
 *
 * The transfers (#14) keep their own section: moving seats between venues is still a
 * signed, atomically-ledgered operation, and the ledger (#15) is the history of it.
 */
export function QuotasPage() {
  return (
    <>
      <div className="page-header">
        <h1>Квоты</h1>
        <p className="page-header__sub">План площадки по форматам: офлайн и онлайн, переводы мест.</p>
      </div>

      <QuotasView />
    </>
  );
}

// ---------------------------------------------------------------------------
// The grid, the filters, the transfer dialog and the ledger
// ---------------------------------------------------------------------------

function QuotasView() {
  const { params, setParams } = useQueryParams();
  const { role } = useAuth();
  const isAdmin = hasAtLeast(role, 'ADMIN');

  const venueId = toInt(params[PARAM.VENUE_ID]);
  const department = parseDepartmentBucket(params[PARAM.DEPARTMENT]) as DepartmentBucket | undefined;
  const sortField = parseSortField(params[PARAM_SORT]);
  const sortDesc = parseSortDir(params[PARAM_DIR], sortField);

  const plans = useVenuePlans();
  const allRows = plans.data?.data ?? [];

  // The venue directory (names/addresses the combobox searches) also carries the
  // kind/partner facts this page's T.Z149 fold reads: a partner filial holds NO quota
  // of its own — `partners.plan_online` is the ONE shared number — so its grid row
  // misrepresents the group and leaves, and the pool becomes the banner block above.
  const directory = useVenueDirectory();
  const partnerPoolEntries = useMemo(() => partnerPoolsOfDirectory(directory.data ?? []), [directory.data]);
  const partnerVenueIds = useMemo(() => partnerVenueIdsOf(directory.data ?? []), [directory.data]);
  const gridRows = useMemo(() => rowsWithoutPartnerVenues(allRows, partnerVenueIds), [allRows, partnerVenueIds]);

  // ADDITIVE (T.Z56) — `venueId → bucket` off the #6 × #25.5 join (whole roster: an
  // option keeps promising the same venues whatever is selected right now).
  const { buckets: departmentBuckets, counts: departmentCounts } = useDepartmentJoin();

  const rows = useMemo(
    () =>
      rowsInDepartmentBucket(
        venueId === undefined ? gridRows : gridRows.filter((row) => row.venueId === venueId),
        departmentBuckets,
        department,
      ),
    [gridRows, venueId, departmentBuckets, department],
  );
  const sortedRows = useMemo(() => sortPlanRows(rows, sortField, sortDesc), [rows, sortField, sortDesc]);
  const totals = useMemo(() => planTotals(rows), [rows]);
  // The transfer dialog's options live on the same universe the grid does (T.Z149): a
  // filial's plan is not its own to move. The NAMES still resolve off `allRows`, so a
  // ledger row that mentions a filial never degrades to «Площадка 232».
  const venues = useMemo(() => planVenueOptions(gridRows), [gridRows]);

  const setFilter = (key: string, value: ParamValue) => setParams({ [key]: value });

  const sort = (field: PlanSortField) => {
    const active = field === sortField;
    // The first click on a plan column puts the biggest plans on top; the active
    // column flips. The direction is always written out: a bare link still gets the
    // column's own default, but a flipped column must not read as that default.
    const descending = active ? !sortDesc : firstClickDirection(field);
    setParams({ [PARAM_SORT]: field, [PARAM_DIR]: descending ? 'desc' : 'asc' });
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [lastTransfer, setLastTransfer] = useState<TransferDto | null>(null);

  // The venue combobox is a type-ahead over the venue DICTIONARY (names and addresses,
  // the same read the venues/applications filters use — the read `partnerPoolEntries`
  // above already holds); the grid's own venues stand in until it loads, so the filter
  // never reads as «nothing to pick».
  const choices = useMemo(() => {
    const fromDictionary = directory.data ? venueComboboxOptions(directory.data) : [];
    const options = fromDictionary.length > 0 ? fromDictionary : venues.map((option) => ({ value: String(option.id), label: option.name }));
    if (venueId === undefined || options.some((option) => option.value === String(venueId))) return options;
    return [{ value: String(venueId), label: venueNameOf(allRows, venueId) }, ...options];
  }, [directory.data, venues, venueId, allRows]);

  return (
    <>
      {plans.isPending ? (
        <GridSkeleton />
      ) : plans.isError ? (
        <div className="card">
          <ErrorState
            message={plans.error instanceof Error ? plans.error.message : 'Не удалось загрузить квоты.'}
            onRetry={() => void plans.refetch()}
          />
        </div>
      ) : (
        <>
          {/* ADDITIVE (T.Z149, owner 20.09) — the partner pools as a notification block,
              not grid rows: a filial holds no quota of its own, the partner holds ONE.
              The pencil-and-number edit reuses #29 — the exact write the settings tab
              makes — so the quota is changed where it is now read. */}
          {partnerPoolEntries.length > 0 ? (
            <div className="quotas-pool-banner" data-testid="partner-pool-banner">
              {partnerPoolEntries.map((entry) => (
                <PartnerPoolBanner key={entry.partnerId} entry={entry} canEdit={isAdmin} />
              ))}
            </div>
          ) : null}

          <div className="card card--flush">
            <div className="quotas-pagehead" data-testid="quotas-pagehead">
              <p className="quotas-toolbar__meta" data-testid="quotas-meta">
                {rows.length} площадок · план офл/онл
                {department !== undefined ? ` · ${DEPARTMENT_BUCKET_LABEL[department]}` : ''}
              </p>
              <div className="quotas-pagehead__actions">
                {isAdmin ? (
                  <Button variant="primary" data-testid="open-transfer" onClick={() => setDialogOpen(true)}>
                    Передать места
                  </Button>
                ) : null}
                {/* T.E1 — the grid as a file, scoped by the venue the grid is scoped by. */}
                <ExportButton resource="quotas" testId="quotas-export" params={{ venueId }} />
              </div>
            </div>

            <div className="quotas-toolbar quotas-toolbar--filters">
              <div className="quotas-toolbar__field quotas-toolbar__field--venue">
                <label className="field__label" htmlFor="quotas-venue">
                  Площадка
                </label>
                <Combobox
                  id="quotas-venue"
                  testId="filter-venue"
                  value={venueId !== undefined ? String(venueId) : ''}
                  options={choices}
                  onChange={(next) => setFilter(PARAM.VENUE_ID, next === '' ? undefined : next)}
                  placeholder="Все площадки — найдите по названию или адресу"
                  emptyText="Площадка не найдена"
                  clearLabel="Показать все площадки"
                  resultsLabel={(count) => `Найдено площадок: ${count}`}
                />
              </div>
              {/* ADDITIVE (T.Z56) — «Отдел»: client-side like the combobox above. */}
              <div className="quotas-toolbar__field">
                <label className="field__label" htmlFor="quotas-department">
                  Отдел
                </label>
                <Select
                  id="quotas-department"
                  data-testid="quotas-department"
                  value={department ?? ''}
                  onChange={(event) => setFilter(PARAM.DEPARTMENT, event.target.value || undefined)}
                >
                  {departmentFilterOptions(departmentCounts).map((option) => (
                    <option key={option.value} value={option.value} title={option.title}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {rows.length === 0 ? (
              <EmptyState
                title="Квот не найдено"
                hint={
                  venueId !== undefined
                    ? partnerVenueIds.has(venueId)
                      ? 'У филиалов партнёра собственной квоты нет — их общая квота наверху страницы.'
                      : 'По этому фильтру квот нет — ослабьте условие.'
                    : 'Квоты появятся после импорта «Квоты по площадкам» или первого заданного плана.'
                }
              />
            ) : (
              <PlanGrid rows={sortedRows} totals={totals} editable={isAdmin} sortField={sortField} sortDesc={sortDesc} onSort={sort} />
            )}
          </div>

          <TransfersSection venueId={venueId} nameOf={(id) => venueNameOf(allRows, id)} canTransfer={isAdmin} lastTransfer={lastTransfer} />

          {dialogOpen ? (
            <TransferDialog
              venues={venues}
              nameOf={(id) => venueNameOf(allRows, id)}
              onClose={() => setDialogOpen(false)}
              onCreated={(transfer) => {
                setLastTransfer(transfer);
                setDialogOpen(false);
              }}
            />
          ) : null}
        </>
      )}
    </>
  );
}

/**
 * ADDITIVE (T.Z149, owner 20.09) — one partner's shared-quota block: «Академия Топ:
 * общая квота 2 600», plus — for an ADMIN — the number changed right here. The write is
 * #29 (`PATCH /api/partners/:partnerId`), the SAME audited ADMIN-only write the settings
 * tab makes and the same validation (`parsePoolInput`, an empty draft is not a 0); the
 * hook invalidates the venue caches, this page's directory among them, so the caption
 * and every read that seeds from it move together.
 */
function PartnerPoolBanner({ entry, canEdit }: { entry: PartnerPoolEntry; canEdit: boolean }) {
  const [draft, setDraft] = useState<string | null>(null);
  const save = usePartnerPoolPatch();
  // The api answers the live prefill count with the write; until the first save this
  // block cannot know it and warns on nothing — the settings block's own rule.
  const used = save.data?.used;

  const parsed = parsePoolInput(draft ?? String(entry.planOnline));
  const changed = poolChanged(entry.planOnline, parsed.value);
  const warning = poolLowWarning(parsed.value, used);

  const submit = () => {
    if (parsed.value === null || !changed || save.isPending) return;
    save.mutate({ partnerId: entry.partnerId, planOnline: parsed.value });
  };

  return (
    <div className="quotas-pool-banner__item" data-testid="partner-pool-item" data-partner-id={entry.partnerId}>
      <p className="quotas-pool-banner__caption" data-testid="partner-pool-caption">
        {partnerPoolCaption(entry.partnerName, entry.planOnline)}
      </p>
      <p className="quotas-pool-banner__meta" data-testid="partner-pool-meta">
        {entry.venues} филиалов · квота общая, у филиалов собственной нет
        {used !== undefined ? ` · использовано ${used.toLocaleString('ru-RU')}` : ''}
      </p>
      {canEdit ? (
        <div className="quotas-pool-banner__edit">
          <Input
            data-testid="partner-pool-input"
            aria-label={`Общая квота — ${entry.partnerName}`}
            inputMode="numeric"
            value={draft ?? String(entry.planOnline)}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                submit();
              }
            }}
          />
          <Button
            variant="primary"
            size="sm"
            data-testid="partner-pool-save"
            loading={save.isPending}
            disabled={!changed || parsed.value === null}
            onClick={submit}
          >
            Сохранить
          </Button>
        </div>
      ) : null}
      {parsed.error !== undefined ? (
        <p className="quotas-pool-banner__error" role="alert">
          {parsed.error}
        </p>
      ) : warning !== null ? (
        <p className="quotas-pool-banner__error" role="alert" data-testid="partner-pool-warning">
          {warning}
        </p>
      ) : save.isError ? (
        <p className="quotas-pool-banner__error" role="alert">
          {save.error instanceof Error ? save.error.message : 'Не удалось сохранить квоту.'}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The URL state of the grid
// ---------------------------------------------------------------------------

const PARAM_SORT = 'sort';
const PARAM_DIR = 'dir';

function parseSortField(value: ParamValue | undefined): PlanSortField {
  const text = typeof value === 'string' ? value : '';
  return (PLAN_SORT_FIELDS as readonly string[]).includes(text) ? (text as PlanSortField) : 'name';
}

/** A plan column starts `desc` on its first click; a name column starts `asc`. */
function parseSortDir(value: ParamValue | undefined, field: PlanSortField): boolean {
  if (value === 'desc') return true;
  if (value === 'asc') return false;
  return firstClickDirection(field);
}

// ---------------------------------------------------------------------------
// The grid
// ---------------------------------------------------------------------------

/**
 * `Площадка | План офлайн | План онлайн | Итого` — the owner's own sheet, one row per
 * place. The two plan columns are the same write the venue card exposes (PATCH #8),
 * so an admin edits the plan where the plan is read; a VIEWER sees the numbers only.
 * The venue name is a LINK to the place's page: the grid answers «сколько», the card
 * answers «почему».
 */
function PlanGrid({
  rows,
  totals,
  editable,
  sortField,
  sortDesc,
  onSort,
}: {
  rows: QuotaGridRow[];
  totals: { offline: number; online: number; total: number };
  editable: boolean;
  sortField: PlanSortField;
  sortDesc: boolean;
  onSort: (field: PlanSortField) => void;
}) {
  return (
    <TableWrap>
      <table className="data quota-grid" data-testid="quota-grid">
        <thead>
          <tr>
            <SortableHead
              label="Площадка"
              field="name"
              sortField={sortField}
              sortDesc={sortDesc}
              onSort={onSort}
              className="quota-grid__venue"
              testId="quota-sort-name"
            />
            {PLAN_COLUMNS.map((column) => (
              <SortableHead
                key={column.key}
                label={column.label}
                field={column.key}
                sortField={sortField}
                sortDesc={sortDesc}
                onSort={onSort}
                className="num quota-grid__plan-head"
                testId={`quota-sort-${column.key.toLowerCase()}`}
              />
            ))}
            <th scope="col" className="num quota-grid__total-head">
              Итого
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.venueId} data-testid="quota-row" data-venue-id={row.venueId}>
              <th scope="row" className="quota-grid__venue" title={row.venueName}>
                <Link className="quota-grid__venue-link" to={venueCardHref(row.venueId)} data-testid="quota-venue-link">
                  {row.venueName}
                </Link>
              </th>
              <PlanCell row={row} field="planOffline" editable={editable} />
              <PlanCell row={row} field="planOnline" editable={editable} />
              <td className="num quota-grid__total" data-testid="row-total">
                {formatCount(row.total)}
              </td>
            </tr>
          ))}
          <tr className="quota-grid__totals" data-testid="quota-totals-row">
            <th scope="row" className="quota-grid__venue">
              Итого
            </th>
            <td className="num quota-grid__total" data-testid="column-total-planoffline">
              {formatCount(totals.offline)}
            </td>
            <td className="num quota-grid__total" data-testid="column-total-planonline">
              {formatCount(totals.online)}
            </td>
            <td className="num quota-grid__total" data-testid="grid-grand-total">
              {formatCount(totals.total)}
            </td>
          </tr>
        </tbody>
      </table>
    </TableWrap>
  );
}

/** A sortable header: a button with `aria-sort`, the active one marked. */
function SortableHead({
  label,
  field,
  sortField,
  sortDesc,
  onSort,
  className,
  testId,
}: {
  label: string;
  field: PlanSortField;
  sortField: PlanSortField;
  sortDesc: boolean;
  onSort: (field: PlanSortField) => void;
  className: string;
  testId: string;
}) {
  const active = field === sortField;
  return (
    <th scope="col" className={className} aria-sort={active ? (sortDesc ? 'descending' : 'ascending') : undefined}>
      <button
        type="button"
        className={active ? 'quota-sort quota-sort--active' : 'quota-sort'}
        data-testid={testId}
        title={`Сортировать по «${label}»`}
        onClick={() => onSort(field)}
      >
        {label}
        <span className="quota-sort__arrow" aria-hidden="true">
          {active ? (sortDesc ? '↓' : '↑') : '↕'}
        </span>
      </button>
    </th>
  );
}

/**
 * One plan cell. An ADMIN gets an input (commit on blur or Enter, the same rules the
 * api applies: a non-negative integer, an empty draft is not a zero), a VIEWER gets the
 * number. The save is PATCH #8 on the venue, so the card, the roster and this grid all
 * read the same two numbers back.
 */
function PlanCell({ row, field, editable }: { row: QuotaGridRow; field: 'planOffline' | 'planOnline'; editable: boolean }) {
  const current = row[field];
  const [draft, setDraft] = useState<string | null>(null);
  // Escape has to REVERT, not commit: the flag is read by the blur handler the very
  // next tick, where a state-only reset would still see the stale draft.
  const reverted = useRef(false);
  const save = useVenuePlanSave(row.venueId, field);

  const cellId = field === 'planOffline' ? 'quota-plan-offline' : 'quota-plan-online';
  const inputId = field === 'planOffline' ? 'quota-input-offline' : 'quota-input-online';

  if (!editable) {
    return (
      <td className="num quota-cell" data-testid={cellId} data-venue-id={row.venueId}>
        {formatCount(current)}
      </td>
    );
  }

  const value = draft ?? String(current);
  const parsed = parsePlanInput(value);
  const invalid = draft !== null && parsed.error !== undefined;

  const commit = () => {
    // An Escape before the blur: the draft is dropped, nothing travels.
    if (reverted.current) {
      reverted.current = false;
      setDraft(null);
      return;
    }
    if (draft === null) return;
    const parsedDraft = parsePlanInput(draft);
    if (parsedDraft.value === null) {
      // A junk draft STAYS on screen with its error — silently reverting it would
      // read as "saved". Escape (or typing over it) is the way out.
      return;
    }
    if (planChanged(current, parsedDraft.value)) save.mutate(parsedDraft.value);
    setDraft(null);
  };

  return (
    <td className="num quota-cell quota-cell--edit" data-venue-id={row.venueId} data-testid={cellId}>
      <input
        className={invalid ? 'input quota-plan-input quota-plan-input--invalid' : 'input quota-plan-input'}
        data-testid={inputId}
        inputMode="numeric"
        aria-label={`${field === 'planOffline' ? 'План офлайн' : 'План онлайн'} — ${row.venueName}`}
        value={value}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            event.currentTarget.blur();
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            reverted.current = true;
            event.currentTarget.blur();
          }
        }}
      />
      {save.isPending ? (
        <span className="quota-cell__spinner" aria-hidden="true" />
      ) : invalid ? (
        <span className="quota-cell__error" role="alert" data-testid="quota-plan-error">
          {parsed.error}
        </span>
      ) : save.isError ? (
        <span className="quota-cell__error" role="alert">
          {save.error instanceof Error ? save.error.message : 'Не удалось сохранить план.'}
        </span>
      ) : null}
    </td>
  );
}

/**
 * The plan write, OPTIMISTIC across every cached venue-plans read: the cell shows the
 * typed number the moment it commits, and a failure rolls the cache back and hands the
 * api's message to the cell (403 below ADMIN, 400 for a junk value, 404 for a venue
 * that is gone).
 */
function useVenuePlanSave(venueId: number, field: 'planOffline' | 'planOnline') {
  const client = useQueryClient();

  return useMutation({
    // PATCH #8 — the venue's own write. `0` is a value here, not a clear: the plan of a
    // place is a number, and «no seats of this format» is exactly 0.
    mutationFn: (plan: number) => patchVenue(venueId, { [field]: plan }),
    onMutate: async (plan: number) => {
      await client.cancelQueries({ queryKey: VENUE_PLANS_ROOT });
      const entries = client.getQueriesData<VenuePlanListEnvelope>({ queryKey: VENUE_PLANS_ROOT });
      for (const [key, value] of entries) {
        if (!value) continue;
        client.setQueryData<VenuePlanListEnvelope>(key, {
          ...value,
          data: value.data.map((row) => {
            if (row.venueId !== venueId) return row;
            const next = { ...row, [field]: plan } as VenuePlanDto;
            return { ...next, total: next.planOffline + next.planOnline };
          }),
        });
      }
      return { entries };
    },
    onError: (_error, _plan, context) => {
      for (const [key, value] of context?.entries ?? []) {
        client.setQueryData(key, value);
      }
    },
    onSettled: () => {
      // The server's answer is the authority: the patch also touches the venue's
      // own caches (card, roster), which read `planOffline` off the VenueDto.
      void client.invalidateQueries({ queryKey: VENUE_PLANS_ROOT });
      void client.invalidateQueries({ queryKey: ['venues'] });
    },
  });
}

/**
 * The api's refusal of a plan edit (400 junk body, 403 below ADMIN, 404 unknown
 * venue/program) — one honest line above the grid, while the optimistic rollback
 * puts the server value back in the cell.
 */
function TransfersSection({
  venueId,
  nameOf,
  canTransfer,
  lastTransfer,
}: {
  venueId: number | undefined;
  nameOf: (venueId: number) => string;
  canTransfer: boolean;
  lastTransfer: TransferDto | null;
}) {
  const ledger = useTransfers({ venueId });
  const rows = ledger.data?.data ?? [];

  return (
    <div className="card card--flush">
      <div className="quotas-toolbar">
        <h2 className="quotas-toolbar__title">Переводы мест</h2>
        <p className="quotas-toolbar__meta" data-testid="ledger-meta">
          {venueId !== undefined ? 'записи, где площадка участвует любой стороной' : 'все переводы, новые сверху'}
        </p>
      </div>

      {lastTransfer ? (
        <p className="quotas-flash" data-testid="transfer-created" role="status">
          Перевод №{lastTransfer.id} записан: {formatCount(lastTransfer.amount)} мест ·{' '}
          {transferScopeLabel(lastTransfer)} · {transferRoute(lastTransfer, nameOf)}
        </p>
      ) : null}

      {ledger.isPending ? (
        <LedgerSkeleton />
      ) : ledger.isError ? (
        <div className="quotas-ledger-state">
          <ErrorState
            message={ledger.error instanceof Error ? ledger.error.message : 'Не удалось загрузить переводы.'}
            onRetry={() => void ledger.refetch()}
          />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="Переводов пока не было"
          hint={
            canTransfer
              ? 'Передайте места между площадками — обе стороны плана и запись в журнале создадутся одним действием.'
              : 'Переводы выполняет администратор; здесь видна история.'
          }
        />
      ) : (
        <TableWrap>
          <table className="data" data-testid="ledger-table">
            <thead>
              <tr>
                <th scope="col">Когда</th>
                <th scope="col">Направление</th>
                <th scope="col" className="num">
                  Мест
                </th>
                <th scope="col">Область</th>
                <th scope="col">Причина</th>
                <th scope="col" className="num">
                  №
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((transfer) => {
                const direction = transferDirection(transfer, venueId);
                return (
                  <tr key={transfer.id} data-testid="ledger-row" data-transfer-id={transfer.id}>
                    <td data-testid="ledger-when">{formatDateTime(transfer.createdAt)}</td>
                    <td>
                      <span className="ledger-route" data-testid="ledger-route">
                        {transferRoute(transfer, nameOf)}
                      </span>{' '}
                      {direction !== 'OTHER' ? (
                        <span data-testid="ledger-direction" data-direction={direction}>
                          <Badge tone={direction === 'OUTGOING' ? 'warn' : 'ok'}>
                            {transferDirectionLabel(direction)}
                          </Badge>
                        </span>
                      ) : null}
                    </td>
                    <td className="num" data-testid="ledger-amount">
                      {formatCount(transfer.amount)}
                    </td>
                    <td data-testid="ledger-scope">{transferScopeLabel(transfer)}</td>
                    <td className="ledger-reason" data-testid="ledger-reason" title={transfer.reason}>
                      {transfer.reason}
                    </td>
                    <td className="num cell-muted">{transfer.id}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableWrap>
      )}
    </div>
  );
}

function TransferDialog({
  venues,
  nameOf,
  onClose,
  onCreated,
}: {
  venues: IdNameOption[];
  nameOf: (venueId: number) => string;
  onClose: () => void;
  onCreated: (transfer: TransferDto) => void;
}) {
  const [draft, setDraft] = useState<TransferDraft>(emptyTransferDraft);
  // Errors appear per field, as it is touched: a freshly opened dialog must not
  // shout four complaints at an empty form.
  const [touched, setTouched] = useState<Partial<Record<keyof TransferDraft, boolean>>>({});
  const validation = useMemo(() => validateTransferDraft(draft), [draft]);
  const create = useTransferCreate();

  const set = <K extends keyof TransferDraft>(key: K, value: TransferDraft[K]) => {
    setTouched((current) => ({ ...current, [key]: true }));
    setDraft((current) => ({ ...current, [key]: value }));
  };

  const errorOf = (key: TransferField) => (touched[key] ? validation.errors[key] : undefined);

  const submit = () => {
    if (!validation.canSubmit || validation.body === null || create.isPending) return;
    create.mutate(validation.body, { onSuccess: (transfer) => onCreated(transfer) });
  };

  const fromName = draft.fromVenueId === '' ? '…' : nameOf(Number(draft.fromVenueId));
  const toName = draft.toVenueId === '' ? '…' : nameOf(Number(draft.toVenueId));
  const amount = Number.parseInt(draft.amount, 10);
  const amountReady = Number.isInteger(amount) && amount > 0;
  const formatChosen = draft.format !== '';

  return (
    <Modal
      open
      onClose={onClose}
      title="Передать места"
      description="Обе стороны плана и запись в журнале создаются одной транзакцией."
    >
      <form
        className="transfer-form"
        data-testid="transfer-form"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div className="form-grid">
          <Field label="Площадка-донор" htmlFor="transfer-from" required error={errorOf('fromVenueId')}>
            <Select
              id="transfer-from"
              data-testid="transfer-from"
              value={draft.fromVenueId}
              aria-invalid={errorOf('fromVenueId') ? true : undefined}
              onChange={(event) => set('fromVenueId', event.target.value)}
            >
              <option value="">Выберите площадку</option>
              {venues.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Площадка-получатель" htmlFor="transfer-to" required error={errorOf('toVenueId')}>
            <Select
              id="transfer-to"
              data-testid="transfer-to"
              value={draft.toVenueId}
              aria-invalid={errorOf('toVenueId') ? true : undefined}
              onChange={(event) => set('toVenueId', event.target.value)}
            >
              <option value="">Выберите площадку</option>
              {venues.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="Сколько мест"
            htmlFor="transfer-amount"
            required
            error={errorOf('amount')}
            hint="План донора должен покрывать сумму — иначе 409"
          >
            <Input
              id="transfer-amount"
              data-testid="transfer-amount"
              type="number"
              min={1}
              step={1}
              value={draft.amount}
              aria-invalid={errorOf('amount') ? true : undefined}
              onChange={(event) => set('amount', event.target.value)}
            />
          </Field>

          <Field
            label="Область перевода"
            htmlFor="transfer-scope"
            error={errorOf('scope')}
            hint="Без формата места расходятся по офлайн- и онлайн-плану донора пропорционально"
          >
            <Select
              id="transfer-scope"
              data-testid="transfer-scope"
              value={draft.scope}
              onChange={(event) => {
                const next = event.target.value === 'FORMAT' ? 'FORMAT' : 'VENUE_LEVEL';
                set('scope', next);
                // A scope that NEEDS a format says so the moment it is chosen, not
                // only after the format field was touched.
                if (next === 'FORMAT') setTouched((current) => ({ ...current, format: true }));
              }}
            >
              <option value="VENUE_LEVEL">Вся площадка (пропорционально)</option>
              <option value="FORMAT">Один формат</option>
            </Select>
          </Field>

          {draft.scope === 'FORMAT' ? (
            <Field label="Формат" htmlFor="transfer-format" error={errorOf('format')}>
              <Select
                id="transfer-format"
                data-testid="transfer-format"
                value={draft.format}
                onChange={(event) => set('format', event.target.value as CourseFormat | '')}
              >
                <option value="">Выберите формат</option>
                {COURSE_FORMATS.map((format) => (
                  <option key={format} value={format}>
                    {format === 'OFFLINE' ? 'Офлайн' : 'Онлайн'}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}

          <div className="form-grid__full">
            <Field label="Причина" htmlFor="transfer-reason" required error={errorOf('reason')}>
              <Textarea
                id="transfer-reason"
                data-testid="transfer-reason"
                rows={3}
                value={draft.reason}
                aria-invalid={errorOf('reason') ? true : undefined}
                onChange={(event) => set('reason', event.target.value)}
                placeholder="Например: набор на офлайн-курс в Кемерово не собрался"
              />
            </Field>
          </div>
        </div>

        <p className="transfer-form__summary" data-testid="transfer-summary">
          {draft.scope === 'FORMAT'
            ? `${amountReady ? amount : '?'} мест · ${
                formatChosen ? (draft.format === 'OFFLINE' ? 'офлайн' : 'онлайн') : 'формат не выбран'
              }: ${fromName} → ${toName}.`
            : amountReady
              ? venueLevelHint(amount, fromName, toName)
              : `${fromName} → ${toName}.`}
        </p>

        {create.isError ? (
          <p className="transfer-form__error" role="alert" data-testid="transfer-server-error">
            {create.error instanceof Error ? create.error.message : 'Не удалось выполнить перевод.'}
          </p>
        ) : null}

        <footer className="transfer-form__footer">
          <Button type="button" size="sm" onClick={onClose} data-testid="transfer-cancel">
            Отмена
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            data-testid="transfer-submit"
            loading={create.isPending}
            disabled={!validation.canSubmit}
          >
            Передать места
          </Button>
        </footer>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Skeletons
// ---------------------------------------------------------------------------

function GridSkeleton() {
  return (
    <div className="card card--flush" data-testid="quotas-skeleton">
      <div className="table-wrap">
        <table className="data" aria-hidden="true">
          <thead>
            <tr>
              <th>Площадка</th>
              <th>План офлайн</th>
              <th>План онлайн</th>
              <th className="num">Итого</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }, (_, index) => (
              <tr key={index}>
                <td>
                  <div className="skeleton skeleton--line skeleton--wide" />
                </td>
                {Array.from({ length: 3 }, (_, cell) => (
                  <td key={cell}>
                    <div className="skeleton skeleton--line skeleton--num" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LedgerSkeleton() {
  return (
    <div className="quotas-ledger-state" data-testid="ledger-skeleton">
      <div className="skeleton skeleton--line skeleton--wide" />
      <div className="skeleton skeleton--line" />
    </div>
  );
}
